/* **** VERY IMPORTANT TO DO **** */
// Whenever a child is loaded from a parent, make sure that we cache that relationship:
// The parent ID and the prop name. This will enable us to go back and modify the parent
// when we get deleted or (in future) updated.

// Also, the cache is still failing to update after expiration. Set the cache time super
// low to debug. Proposition: Set the cache time to less than 5 minutes to prevent
// garbage collection. Alternatively, figure out how to "tap" each cached entry so it
// doesn't get collected.
import {api2Endpoint, apiEndpoint} from 'app/config/apiConfig'
import {CRoot} from 'app/modules/discovery/core'
import {CBaseRecord, CRecordClass, IBaseRecord} from 'app/modules/discovery/core/newmodels/_base'
import {queryClient, queryDefaults} from 'app/react-query'
import {useMutation, useQuery} from 'react-query'
import {
  JsonObject,
  JsonObjectWithId,
  cloneJson,
  isJsonData,
  jsonDataGet,
  jsonDataIsArrayWithIds,
  jsonDataIsObj,
  jsonDataIsObjWithId,
} from 'utilities/types'
import {ParentContext} from './ApiParentContext'
import {ApiRelations} from './ApiRelations'
import {ApiReversibleUpdate, ApiUpdateBatch} from './ApiReversibleUpdate'
import {deleteData} from './api/useDeleteData'
import {getData} from './api/useGetData'
import {postData} from './api/usePostData'
import {putData} from './api/usePutData'
import {announceReturn, announceSend} from './api/utils/announce'
import errorNotify from './api/utils/errorNotify'

type ApiRepoArgs<IType extends IBaseRecord, CClass extends typeof CBaseRecord<IType>> = {
  name: string
  recordClass: CClass
  outputOnModify?: boolean
}

export class ApiRepo<IType extends IBaseRecord, CClass extends typeof CBaseRecord<IType>> {
  name
  recordClass
  timestamp
  _outputOnModify
  _relations

  constructor(args: ApiRepoArgs<IType, CClass>) {
    this.name = args.name
    this.recordClass = args.recordClass
    this.timestamp = Date.now()
    this._outputOnModify = args.outputOnModify
    this._relations = new ApiRelations(this.recordClass)
  }

  _contextFrom(opts?: {parentContext?: ParentContext; data?: any; id?: string}) {
    const {parentContext, data, id} = opts ?? {}
    if (parentContext) {
      return parentContext
    }
    const cachedData = id ? this._relations.retrieve(id) : undefined
    if (cachedData || jsonDataIsObjWithId(data)) {
      const fullData = this.recordClass.merge(cachedData, data ?? {})
      return this.recordClass.guessParentContext(fullData, false)
    }
    return this.recordClass.guessParentContext(data ?? {}, false)
  }

  _childContextFrom(id: string, prop: string) {
    const path = this.recordClass.childPath(prop)
    return new ParentContext({
      parentClass: path.parentClass,
      childClass: path.childClass,
      prop,
      parentId: id,
    })
  }

  // Before we do pretty much anything, ensure that the root record class has
  // registered all descendants.
  // DO NOT call registerWithDescendants() on recordClass.
  // This ensures that if we forgot to register something with CRoot, it will throw
  // a helpful error message instead of a weird cryptic one.
  doRegister() {
    CRoot.registerWithDescendants()
  }

  invalidate(id: string) {
    queryClient.invalidateQueries(['records', id])
    for (const childPath of this.recordClass.childPaths) {
      queryClient.invalidateQueries(['records', id, childPath.prop])
    }
  }

  invalidateRepo() {
    queryClient.invalidateQueries(['records'])
    for (const childPath of this.recordClass.childPaths) {
      queryClient.invalidateQueries(['records', 'root', childPath.prop])
    }
  }

  createReversibleUpdate(id: string, updateFunc?: (original: any) => any) {
    return new ApiReversibleUpdate(['records', id], this.recordClass, updateFunc)
  }

  route(args?: {recordClass?: CRecordClass; id?: string; prop?: string; childId?: string}) {
    const {id, prop, childId} = args ?? {}
    const recordClass = (args ?? {}).recordClass ?? this.recordClass

    let output = 'std'
    if (recordClass.isRoot) {
      if (prop === undefined) {
        throw new Error(`Must specify a property when computing a route from CRoot.`)
      }
      const path = recordClass.childPath(prop)
      output = `std/${path.childClass.record_name}`
    } else {
      output = `std/${recordClass.record_name}`
      if (id !== undefined) {
        output = `${output}/${id}`
      }
      if (prop !== undefined) {
        output = `${output}/${prop}`
      }
    }
    if (childId !== undefined) {
      output = `${output}/${childId}`
    }
    return output
  }

  useRead(
    id?: string,
    opts?: {
      parentContext?: ParentContext
      children?: string[]
      disabled?: boolean
      default?: IType & JsonObjectWithId
    }
  ) {
    return this._useRead(id, opts)
  }

  _useRead(
    id?: string,
    opts?: {
      parentContext?: ParentContext
      children?: string[]
      disabled?: boolean
      default?: IType & JsonObjectWithId
    }
  ) {
    this.doRegister()
    const {parentContext} = opts ?? {}
    return useQuery({
      queryKey: ['records', id],
      queryFn: async () => {
        if (id === undefined || id === null || id === '') {
          return undefined
        }
        try {
          this.timestamp = Date.now()
          const route = this.route({recordClass: this.recordClass, id})
          if (route.includes('std/tenant/')) {
            console.log(
              `ERROR CALL here, but from where and axiosConfig is: ${JSON.stringify(
                parentContext?.axiosConfig ?? {}
              )}`
            )
          }
          announceSend('GET', route)
          const response = await getData<unknown>(
            api2Endpoint(route),
            undefined,
            parentContext?.axiosConfig
          )
          announceReturn('GET', route)
          if (!isJsonData(response) || !jsonDataIsObjWithId(response)) {
            throw new Error(
              `Expected ${route} to return JSON-serializable object with id, got: ${JSON.stringify(
                response
              )}`
            )
          }
          this._relations.load(response)
          return response
        } catch (err) {
          errorNotify(err, `Failed to retrieve ${this.recordClass.name} data.`)
        } finally {
          this.timestamp = Date.now()
        }
      },
      select: (data) => new this.recordClass(data as JsonObject & IType) as InstanceType<CClass>,
      enabled: !opts?.disabled,
      cacheTime: queryDefaults.cacheTime,
      staleTime: queryDefaults.staleTime,
    })
  }

  useCreate(opts?: {parentContext?: ParentContext}) {
    return this._useCreate(opts)
  }

  _useCreate(opts?: {parentContext?: ParentContext}) {
    this.doRegister()
    return useMutation(
      async (args: {
        data: IType
        parentContext?: ParentContext
        noAutoCommit?: boolean
        overrideRoute?: string
        noReturn?: boolean
        oldApi?: boolean
      }) => {
        const parentContext = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          data: args.data as unknown as JsonObject,
        })
        const axiosConfig = parentContext?.axiosConfig
        const {data} = args
        if (!isJsonData(data) || !jsonDataIsObj(data)) {
          throw new Error(`Expected JSON-serializable object, got: ${data}`)
        }
        try {
          const route = args?.overrideRoute ? args.overrideRoute : this.route()
          const apiChoice = args?.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          this.timestamp = Date.now()
          announceSend('POST', apiChoice, data)

          const response = await postData<unknown>(apiChoice, data, undefined, axiosConfig)
          announceReturn('POST', route, data)

          if (args.noReturn) {
            return
          }

          let id: string | undefined
          let createdRecord: JsonObject

          if (isJsonData(response) && jsonDataIsObjWithId(response)) {
            createdRecord = response
            id = response.id
          } else if (typeof response === 'string') {
            id = response
            createdRecord = cloneJson(data)
            createdRecord['id'] = id
          } else {
            throw new Error(
              `Expected ${route} to return string or object with "id" prop, got: ${JSON.stringify(
                response
              )}`
            )
          }
          this._relations.create(createdRecord as JsonObjectWithId, {
            parentContext,
            autocommit: args?.noAutoCommit ? false : true,
          })
          return createdRecord as unknown as IType & JsonObjectWithId
        } catch (err) {
          errorNotify(err, `Failed to create ${this.name}.`)
        } finally {
          this.timestamp = Date.now()
        }
      }
    )
  }

  useUpdate(opts?: {parentContext?: ParentContext}) {
    return this._useUpdate(opts)
  }

  _useUpdate(opts?: {parentContext?: ParentContext}) {
    this.doRegister()
    return useMutation(
      async (args: {
        id: string
        data: Partial<IType> & JsonObject
        parentContext?: ParentContext
        overrideRoute?: string
        overrideAction?: 'PUT' | 'POST'
        oldApi?: boolean
      }) => {
        const parent = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          id: args.id,
          data: args.data,
        })
        const axiosConfig = parent?.axiosConfig
        const action = args.overrideAction ?? 'PUT'
        const id = args.id
        const data = cloneJson(args.data)
        data.id = id
        const updates = this._relations.update(id, data) //ApiRecordRegistry.add({recordClass: this.recordClass, data, parent})
        try {
          this.timestamp = Date.now()

          // Do an optimistic update.
          updates.apply()

          const route = args.overrideRoute ?? this.route({id: data.id})
          const apiRoute = args.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          announceSend(action, apiRoute, data)

          const response =
            action === 'PUT'
              ? await putData<unknown>(apiRoute, data, axiosConfig)
              : await postData<unknown>(apiRoute, data, undefined, axiosConfig)
          announceReturn(action, route, data)

          updates.commit()

          return (isJsonData(response) && jsonDataIsObjWithId(response)
            ? response
            : data) as unknown as IType | undefined
        } catch (err) {
          updates.rollback()
          errorNotify(err, `Failed to update ${this.name}.`)
        } finally {
          this.timestamp = Date.now()
        }
      }
    )
  }

  useDelete(opts?: {parentContext?: ParentContext}) {
    return this._useDelete(opts)
  }

  _useDelete(opts?: {parentContext?: ParentContext}) {
    this.doRegister()
    return useMutation(
      async (args: {id: string; parentContext?: ParentContext; oldApi?: boolean}) => {
        const pCtxt = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          id: args.id,
        })
        const axiosConfig = pCtxt?.axiosConfig
        const {id} = args
        const updates = this._relations.remove(id)
        try {
          const route = this.route({id})
          const apiRoute = args?.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          updates.apply()
          announceSend('DELETE', apiRoute)

          await deleteData<string>(apiRoute, axiosConfig)
          announceReturn('DELETE', apiRoute)
          updates.commit()
          return id
        } catch (err) {
          updates.rollback()
          errorNotify(err, `Failed to delete ${this.name}.`)
        }
      }
    )
  }

  useAddChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    return this._useAddChild(opts)
  }

  _useAddChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    this.doRegister()
    return useMutation(
      async (args: {
        prop?: string
        parentId: string
        childId: string
        data?: any
        overrideRoute?: string
        parentContext?: ParentContext
        oldApi?: boolean
        postOrPut?: 'POST' | 'PUT'
      }) => {
        const parentContext = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          id: args.parentId,
        })
        const prop = args?.prop ?? opts?.prop
        if (!prop) {
          throw new Error(
            `useAddChild() requires a value for "prop" either when the hook is created or when it is mutated.`
          )
        }
        const childClass = this.recordClass.childClass(prop)
        const axiosConfig = parentContext?.axiosConfig
        const {parentId: id, childId} = args
        const relData: JsonObjectWithId = {...args.data, ...{id: childId}}
        const childData = new ApiRelations(childClass).retrieve(childId) ?? relData

        let parentUpdate = this._relations.retrieve(id)
        if (jsonDataIsObjWithId(parentUpdate)) {
          parentUpdate = this.recordClass.mergeChild(parentUpdate, prop, 'add', childData)
        }
        const updates = parentUpdate
          ? this._relations.update(id, parentUpdate)
          : new ApiUpdateBatch([])

        try {
          this.timestamp = Date.now()
          const route = args?.overrideRoute ? args.overrideRoute : this.route({id, prop, childId})

          updates.apply()
          announceSend('POST', route, relData ?? {})

          const routeNewOrOldApi = args.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          if (args?.postOrPut === 'PUT') {
            const response = await putData<unknown>(routeNewOrOldApi, relData ?? {}, axiosConfig)
            announceReturn('PUT', route, relData ?? {})
            updates.commit()
            return this._outputOnModify ? response : childId
          } else {
            const response = await postData<unknown>(
              routeNewOrOldApi,
              relData ?? {},
              undefined,
              axiosConfig
            )
            announceReturn('POST', route, relData ?? {})
            updates.commit()
            return this._outputOnModify ? response : childId
          }
        } catch (err) {
          updates.rollback()
          errorNotify(err, `Failed to add item to ${this.name} ${prop}.`)
        } finally {
          this.timestamp = Date.now()
        }
      }
    )
  }

  useUpdateChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    return this._useUpdateChild(opts)
  }

  _useUpdateChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    this.doRegister()
    return useMutation(
      async (args: {
        prop?: string
        parentId: string
        childId: string
        data?: JsonObject
        parentContext?: ParentContext
        oldApi?: boolean
        overrideRoute?: string
      }) => {
        const parentContext = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          id: args.parentId,
        })
        const prop = args?.prop ?? opts?.prop
        if (!prop) {
          throw new Error(
            `useAddChild() requires a value for "prop" either when the hook is created or when it is mutated.`
          )
        }
        const childClass = this.recordClass.childClass(prop)
        const axiosConfig = parentContext?.axiosConfig
        const {parentId: id, childId} = args
        const relData: JsonObjectWithId = {...args.data, ...{id: childId}}
        const childData = new ApiRelations(childClass).retrieve(childId) ?? relData

        let parentUpdate = this._relations.retrieve(id)
        if (jsonDataIsObjWithId(parentUpdate)) {
          parentUpdate = this.recordClass.mergeChild(parentUpdate, prop, 'add', childData)
        }
        const updates = parentUpdate
          ? this._relations.update(id, parentUpdate)
          : new ApiUpdateBatch([])

        try {
          this.timestamp = Date.now()
          const route = args?.overrideRoute ? args.overrideRoute : this.route({id, prop, childId})
          const apiChoice = args?.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          updates.apply()
          announceSend('PUT', apiChoice, relData ?? {})

          const response = await putData<unknown>(apiChoice, relData ?? {}, axiosConfig)
          announceReturn('PUT', apiChoice, relData ?? {})
          updates.commit()
          return this._outputOnModify ? response : childId
        } catch (err) {
          updates.rollback()
          errorNotify(err, `Failed to add item to ${this.name} ${prop}.`)
        } finally {
          this.timestamp = Date.now()
        }
      }
    )
  }

  useRemoveChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    return this._useRemoveChild(opts)
  }

  _useRemoveChild(opts?: {parentContext?: ParentContext; prop?: string}) {
    this.doRegister()
    return useMutation(
      async (args: {
        prop?: string
        parentId: string
        childId: string
        parentContext: ParentContext
        overrideRoute?: string
        oldApi?: boolean
      }) => {
        const prop = args?.prop ?? opts?.prop
        if (!prop) {
          throw new Error(
            `useRemoveChild() requires a value for "prop" either when the hook is created or when it is mutated.`
          )
        }
        const parentContext = this._contextFrom({
          parentContext: args?.parentContext ?? opts?.parentContext,
          id: args.parentId,
        })
        const axiosConfig = parentContext?.axiosConfig
        const {parentId: id, childId} = args

        let parentUpdate = this._relations.retrieve(id)
        if (jsonDataIsObjWithId(parentUpdate)) {
          parentUpdate = this.recordClass.mergeChild(parentUpdate, prop, 'remove', {id: childId})
        }
        const updates = parentUpdate
          ? this._relations.update(id, parentUpdate)
          : new ApiUpdateBatch([])

        try {
          this.timestamp = Date.now()
          const route = args?.overrideRoute ? args?.overrideRoute : this.route({id, prop, childId})
          const apiRoute = args?.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          updates.apply()
          announceSend('DELETE', apiRoute)
          await deleteData<unknown>(apiRoute, axiosConfig)
          announceReturn('DELETE', apiRoute)
          updates.commit()
          queryClient.invalidateQueries(['records', childId])
        } catch (err) {
          updates.rollback()
          errorNotify(err, `Failed to add item to ${this.name} ${prop}.`)
        } finally {
          this.timestamp = Date.now()
        }
      }
    )
  }

  _sortInPlace<OutputType extends JsonObject>(arr: OutputType[], sortKeys?: string[]) {
    const sorters = (sortKeys ?? ['created.at/desc', 'name']).map((sortKey) => {
      const [prop, dir] = sortKey.split('/')
      return {prop, mult: dir === 'desc' ? -1 : 1}
    })

    arr.sort((x, y) => {
      for (const sorter of sorters) {
        const xVal = jsonDataGet(x, sorter.prop, {errorOnFail: false})
        const yVal = jsonDataGet(y, sorter.prop, {errorOnFail: false})
        if (xVal === yVal) {
          continue
        }
        if (yVal === undefined || yVal === null) {
          return -1 * sorter.mult
        }
        if (xVal === undefined || xVal === null) {
          return sorter.mult
        }
        return (xVal < yVal ? -1 : 1) * sorter.mult
      }
      return 0
    })
  }

  useList(args?: {
    parentContext?: ParentContext
    sort?: string | string[]
    filter?: (item: JsonObjectWithId) => boolean
    children?: string[]
    disabled?: boolean
    overrideRoute?: string
  }) {
    return this._useList(args)
  }

  _useList(args?: {
    parentContext?: ParentContext
    sort?: string | string[]
    filter?: (item: JsonObjectWithId) => boolean
    children?: string[]
    disabled?: boolean
    overrideRoute?: string
    oldApi?: boolean
  }) {
    console.log(
      `calling _uselist children: ${JSON.stringify(
        args?.children ?? {}
      )} ---- parentContext: ${JSON.stringify(args?.parentContext ?? {})} `
    )
    this.doRegister()
    const parentContext = this._contextFrom({parentContext: args?.parentContext})
    console.log(
      `2nd calling _uselist children: ${JSON.stringify(
        args?.children ?? {}
      )} ---- parentContext: ${JSON.stringify(args?.parentContext ?? {})} `
    )
    if (!parentContext) {
      throw new Error(
        `${this.recordClass.name} is not a top-level class. useList() requires a parentContext.`
      )
    }
    const {sort, filter} = args ?? {}

    return useQuery({
      queryKey: ['records', parentContext.parent.id, parentContext.prop],
      queryFn: async () => {
        try {
          if (!parentContext.parent.id) {
            return undefined
          }
          this.timestamp = Date.now()
          const route = args?.overrideRoute
            ? args.overrideRoute
            : this.route({
                recordClass: parentContext.parent.recordClass,
                id: parentContext.parent.id,
                prop: parentContext.prop,
              })
          const apiRoute = args?.oldApi ? apiEndpoint(route) : api2Endpoint(route)
          announceSend('GET', apiRoute)

          const nonfiltered = await getData<unknown>(apiRoute, undefined, parentContext.axiosConfig)
          const response = Array.isArray(nonfiltered)
            ? nonfiltered.filter((x: any) => x.status !== 'archived')
            : []
          announceReturn('GET', apiRoute)
          if (isJsonData(response) && jsonDataIsArrayWithIds(response)) {
            const relations = new ApiRelations(parentContext.parent.recordClass)
            relations.loadList(parentContext.parent.id, parentContext.prop, response)
          } else {
            throw new Error(
              `Expected ${route} to return array of JSON-serializable objects with ids, got: ${JSON.stringify(
                response
              )}`
            )
          }
          return response
        } catch (err) {
          errorNotify(err, `Failed to retrieve ${this.recordClass.name} data.`)
        } finally {
          this.timestamp = Date.now()
        }
      },
      select: (data) => {
        if (data === undefined) {
          return undefined
        }
        if (!jsonDataIsArrayWithIds(data)) {
          throw new Error(
            `Expected query cache ['records', '${parentContext.parent.id}', '${
              parentContext.prop
            }'] to contain array of JSON-serializable objects with ids, got: ${JSON.stringify(
              data
            )}`
          )
        }
        const output = data.filter(filter ?? (() => true))
        const sortKeys = sort ? (Array.isArray(sort) ? sort : [sort]) : undefined
        this._sortInPlace(output, sortKeys)
        return output.map(
          (x) => new this.recordClass(x as JsonObject & IType)
        ) as InstanceType<CClass>[]
      },
      enabled: !args?.disabled,
      cacheTime: queryDefaults.cacheTime,
      staleTime: queryDefaults.staleTime,
    })
  }
}
